import { RowActions, type RowActionHandlers } from './RowActions'
import { MapPin } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { formatDateForLocale } from '@/lib/dateUtils'
import { usePresence } from '@/contexts/PresenceContext'
import type { RecoverRequest } from '../api'
import { useWorkforce } from './api'
import { PresenceDot } from './CandidateCard'
import type { PresenceState } from './logic'
import { effectiveIsGo } from '../components/WaitGoState'

/**
 * One column template, used by the header row and by every body row.
 *
 * These are separate grid containers, so an `fr` template alone does not align them: `fr`
 * distributes the space left over after each track's min-content, and the action column's
 * min-content (the Assign button plus six icons) is far wider than its fr share. The header,
 * whose last cell is just the word "Status", resolved its tracks differently and the columns
 * drifted apart. minmax(0,…) removes min-content from the calculation and the action column
 * is pinned to the width its contents actually need.
 *
 * Each site is its own pair of grids, so this also stops the columns shifting from one site
 * section to the next as the object names in them get longer or shorter.
 */
const COLS = 'grid-cols-[minmax(0,1.8fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.1fr)_minmax(0,0.9fr)_196px]'

/**
 * The queue grouped by where the work physically is.
 *
 * Supply and demand for one place sit in the same card, which is the whole point: a site header
 * showing four engineers above eleven waiting objects is a different situation from the same
 * eleven objects with nobody there, and the flat table cannot tell those apart.
 */
export function BySiteView({ rows, locale, onAssign, actions, allowBypass = false }: {
  rows: RecoverRequest[]
  locale: string
  onAssign: (row: RecoverRequest) => void
  /** The Queue's per-row actions, so a row means the same thing on every view. */
  actions?: RowActionHandlers
  /** The screen's bypass policy. Without it this view read the DATABASE's answer, which assumes
   *  bypasses are permitted — so it showed Go where the queue, under the same toggle, said Wait. */
  allowBypass?: boolean
}) {
  const { t } = useTranslation()
  const workforce = useWorkforce()
  const presence = usePresence()

  const groups = useMemo(() => {
    const map = new Map<string, { key: string; locationId: number | null; name: string; rows: RecoverRequest[] }>()
    for (const r of rows) {
      const key = String(r.locationId ?? r.location ?? 'none')
      if (!map.has(key)) {
        map.set(key, {
          key, locationId: r.locationId ?? null,
          name: r.location ?? t('assignment.noLocation', 'No location recorded'),
          rows: [],
        })
      }
      map.get(key)!.rows.push(r)
    }
    const ts = (r: RecoverRequest) => (r.forecastStartTime ? new Date(r.forecastStartTime).getTime() : Number.MAX_SAFE_INTEGER)
    // Sections ordered by the earliest thing standing at each site — the place that starts first
    // is the place command and control has to have covered first.
    return [...map.values()]
      .map((g) => ({ ...g, rows: [...g.rows].sort((a, b) => ts(a) - ts(b)) }))
      .sort((a, b) => ts(a.rows[0]) - ts(b.rows[0]))
  }, [rows, t])

  const engineersAt = (locationId: number | null) =>
    (workforce.data?.engineers ?? []).filter((e) => e.locationId === locationId)

  const nearestFor = (locationId: number | null) =>
    workforce.data?.sites.find((s) => s.locationId === locationId)?.nearestCover ?? null

  const when = (v: string | Date | null | undefined) => {
    if (!v) return '—'
    try { return formatDateForLocale(typeof v === 'string' ? v : v.toISOString(), locale,
      { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false }) }
    catch { return '—' }
  }

  if (groups.length === 0) {
    return (
      <div className="rounded-xl border bg-card px-4 py-10 text-center text-sm text-muted-foreground">
        {t('assignment.queueEmpty',
          'Nothing is waiting for an engineer. Objects leave this queue as soon as they are assigned.')}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {groups.map((g) => {
        const here = engineersAt(g.locationId)
        const go = g.rows.filter((r) => effectiveIsGo(r, allowBypass)).length
        const wait = g.rows.length - go
        const nearest = nearestFor(g.locationId)

        return (
          <section key={g.key} className="overflow-hidden rounded-xl border bg-card">
            {/* The site is the SUBJECT of the section, and at text-sm it weighed the same as the
                object names inside it — the eye had nothing to land on when scanning for a place.
                Larger, tighter, with a rule under the whole header.

                The band is mixed from --muted-FOREGROUND, not --muted. --muted is 96% lightness,
                so bg-muted/40 resolved to roughly a 1.5% step off the card and read as no band at
                all; the foreground token is a mid grey in light mode and a light grey in dark, so
                a low alpha of it steps clearly in both themes instead of only one. */}
            <header className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b-2 bg-muted-foreground/15 px-4 py-3.5">
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-muted-foreground" aria-hidden />
                <h3 className="text-base font-bold leading-none tracking-tight">{g.name}</h3>
              </div>
              <div className="text-xs tabular-nums text-muted-foreground">
                <span style={{ color: 'rgb(101,150,58)' }} className="font-semibold">{go} Go</span>
                <span className="mx-1.5">·</span>
                <span style={{ color: 'rgb(180,74,42)' }} className="font-semibold">{wait} Wait</span>
              </div>

              <div className="ml-auto flex flex-wrap items-center gap-1.5">
                {here.length === 0 ? (
                  <span className="flex flex-wrap items-center gap-1.5">
                    <span className="text-xs font-bold" style={{ color: 'rgb(232,96,55)' }}>
                      {t('assignment.noEngineersOnSite', 'No engineers on site')}
                    </span>
                    {nearest && (
                      <span className="text-[11px] text-muted-foreground">
                        {t('assignment.nearestCover', 'nearest cover: {{site}}, ~{{km}} km away',
                          { site: nearest.location ?? '—', km: nearest.distanceKm })}
                      </span>
                    )}
                  </span>
                ) : here.map((e) => (
                  <span key={e.id}
                        className="inline-flex items-center gap-1.5 rounded-full border bg-background px-2 py-0.5 text-[11px]">
                    <PresenceDot state={(presence?.getUserStatus?.(e.id) ?? 'offline') as PresenceState} />
                    <span className="font-medium">{e.name}</span>
                    {e.primaryTeam && <span className="text-muted-foreground">{e.primaryTeam}</span>}
                  </span>
                ))}
              </div>
            </header>

            <div className={cn('grid border-b bg-muted/20 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground', COLS)}>
              <div className="px-3 py-2">{t('recoverRequests.objectID', 'Object')}</div>
              <div className="px-2 py-2">{t('recoverRequests.team', 'Team')}</div>
              <div className="px-2 py-2">{t('recoverRequests.forecastStartTime', 'Forecast start')}</div>
              <div className="px-2 py-2">{t('assignment.dependencies', 'Dependencies')}</div>
              <div className="px-2 py-2">{t('recoverRequests.objectReady', 'Retrieval Status')}</div>
              <div className="px-2 py-2">{t('recoverRequests.status', 'Status')}</div>
            </div>

            {g.rows.map((r) => {
              const isGo = effectiveIsGo(r, allowBypass)
              const byp = Number(r.bypassableOutstanding ?? 0)
              const hard = Number(r.hardOutstanding ?? 0)
              return (
                <div key={`${r.id}-${r.objectID}`}
                     className={cn('grid items-center border-b last:border-0', COLS)}>
                  <div className="min-w-0 px-3 py-2.5">
                    <div className="truncate text-[13px] font-semibold">{r.objectID}</div>
                    <div className="truncate text-[11px] text-muted-foreground">{r.objectDescription}</div>
                  </div>
                  {/* Most objects have no responsible team. That is the normal case here, so it
                      renders as a quiet dash rather than anything that reads like a fault. */}
                  <div className={cn('truncate px-2 py-2.5 text-xs', !r.teamName && 'text-muted-foreground')}>
                    {r.teamName || '—'}
                  </div>
                  <div className="px-2 py-2.5 text-xs tabular-nums">{when(r.forecastStartTime)}</div>
                  <div className="px-2 py-2.5 text-xs">
                    {hard > 0 && <span style={{ color: 'rgb(180,74,42)' }} className="tabular-nums">{hard} hard</span>}
                    {hard > 0 && byp > 0 && <span className="mx-1 text-muted-foreground">·</span>}
                    {byp > 0 && <span style={{ color: 'rgb(146,88,8)' }} className="tabular-nums">{byp} bypassable</span>}
                    {hard === 0 && byp === 0 && <span className="text-muted-foreground">—</span>}
                  </div>
                  {/* The same two strings and the same two colours the Queue column uses.
                      This rendered the HEADER key as the value, so every row read
                      "Retrieval Status" — and a copy still being retrieved read as an em dash
                      rather than saying it was in progress. */}
                  <div className={cn('px-2 py-2.5 text-xs',
                    r.objectReady === 1 ? 'text-green-600' : 'text-amber-600')}>
                    {r.objectReady === 1
                      ? t('recoverRequests.objectReadyStatus')
                      : t('recoverRequests.copyInProgress')}
                  </div>
                  <div className="flex items-center justify-start gap-1 px-2 py-2">
                    <span className="relative inline-flex">
                      {byp > 0 && (
                        <span className="absolute -right-1.5 -top-1.5 z-10 flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1 text-[10px] font-bold tabular-nums text-white ring-2 ring-background"
                              style={{ backgroundColor: 'rgb(245,158,11)' }}>{byp}</span>
                      )}
                      <button
                        type="button"
                        onClick={() => { if (isGo) onAssign(r) }}
                        className="flex h-12 w-12 items-center justify-center rounded text-xs font-semibold text-white"
                        style={{ backgroundColor: isGo ? 'rgb(151,200,100)' : 'rgb(232,96,55)' }}
                      >
                        {isGo ? t('recoverRequests.assign', 'Assign') : 'WAIT'}
                      </button>
                    </span>
                    {actions && <RowActions row={r} handlers={actions} />}
                  </div>
                </div>
              )
            })}
          </section>
        )
      })}
    </div>
  )
}
